import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import Topbar from '../Topbar';

// ─── S2 station list (order: Schorndorf → Filderstadt) ───────────────────────
// Coordinates match the reference SVG exactly (viewBox 0 0 2500 1750)
const S2_STATIONS = [
  { eva: '8005424', name: 'Schorndorf',                cx: 2278.733, cy: 1224.309 },
  { eva: '8006277', name: 'Weiler(Rems)',               cx: 2214.638, cy: 1160.030 },
  { eva: '8006485', name: 'Winterbach(b Schorndorf)',   cx: 2171.908, cy: 1117.177 },
  { eva: '8002240', name: 'Geradstetten',               cx: 2107.813, cy: 1052.898 },
  { eva: '8002448', name: 'Grunbach',                   cx: 2065.083, cy: 1010.044 },
  { eva: '8000934', name: 'Beutelsbach',                cx: 2022.353, cy:  967.191 },
  { eva: '8001785', name: 'Endersbach',                 cx: 1979.623, cy:  924.338 },
  { eva: '8005726', name: 'Stetten-Beinstein',          cx: 1936.893, cy:  881.484 },
  { eva: '8005157', name: 'Rommelshausen',              cx: 1872.798, cy:  817.205 },
  { eva: '8000180', name: 'Waiblingen',                 cx: 1830.066, cy:  774.351 },
  { eva: '8001974', name: 'Fellbach',                   cx: 1738.339, cy:  774.307 },
  { eva: '8005774', name: 'Stuttgart-Sommerrain',       cx: 1631.975, cy:  774.255 },
  { eva: '8004357', name: 'Stuttgart Nürnberger Str.',  cx: 1525.610, cy:  774.203 },
  { eva: '8005769', name: 'Stuttgart-Bad Cannstatt',    cx: 1370.214, cy:  774.124 },
  { eva: '8098096', name: 'Stuttgart Hbf (tief)',       cx: 1198.024, cy:  845.582 },
  { eva: '8006700', name: 'Stuttgart Stadtmitte',       cx: 1148.277, cy:  895.513 },
  { eva: '8006699', name: 'Stuttgart Feuersee',         cx: 1098.529, cy:  945.443 },
  { eva: '8006698', name: 'Stuttgart Schwabstr.',       cx: 1034.904, cy: 1009.161 },
  { eva: '8006513', name: 'Stuttgart Universität',      cx:  949.287, cy: 1095.235 },
  { eva: '8005779', name: 'Stuttgart-Österfeld',        cx:  899.539, cy: 1145.165 },
  { eva: '8005776', name: 'Stuttgart-Vaihingen',        cx:  824.918, cy: 1220.061 },
  { eva: '8005773', name: 'Stuttgart-Rohr',             cx:  775.171, cy: 1269.992 },
  { eva: '8004496', name: 'Oberaichen',                 cx:  824.116, cy: 1349.740 },
  { eva: '8003622', name: 'Leinfelden',                 cx:  866.934, cy: 1392.680 },
  { eva: '8001650', name: 'Echterdingen',               cx:  931.161, cy: 1457.090 },
  { eva: '8005768', name: 'Stuttgart Flughafen/Messe',  cx:  995.389, cy: 1521.500 },
  { eva: '8001984', name: 'Filderstadt',                cx: 1102.434, cy: 1628.851 },
] as const;

// Path data from the reference SVG (5-point polyline, viewBox 2500×1750)
const S2_PATH_D =
  'M 2278.7333,1224.3094 1830.0655,774.35134 1269.2705,774.07389 759.88861,1285.33 1102.4342,1628.8506';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TrainState {
  id: string;
  fromIdx: number;      // current station index
  toIdx: number;        // next station index (when moving)
  dir: 1 | -1;          // +1 = toward Filderstadt, -1 = toward Schorndorf
  depMs: number;        // virtual-clock departure time
  arrMs: number;        // virtual-clock arrival time
  dwelling: boolean;    // true = waiting at station
  dwellUntilMs: number; // virtual-clock time to depart
}

// ─── Constants ────────────────────────────────────────────────────────────────
const TRAVEL_V_MS = 3 * 60 * 1000; // 3 virtual minutes per leg
const DWELL_V_MS  =     20 * 1000; // 20 virtual seconds at station
const N = S2_STATIONS.length;      // 27

function clamp01(x: number) { return Math.max(0, Math.min(1, x)); }

// Build 6 staggered fake trains (3 each direction)
function makeFakeTrains(vNow: number): TrainState[] {
  const fwd: Array<[number, number]> = [[0, 1], [9, 10], [18, 19]];
  const bwd: Array<[number, number]> = [[26, 25], [17, 16], [8, 7]];
  const all = [
    ...fwd.map(([f, t], i) => ({
      id: `S2 1000${i + 1}`, fromIdx: f, toIdx: t, dir:  1 as const,
      depMs: vNow - TRAVEL_V_MS * 0.35, arrMs: vNow + TRAVEL_V_MS * 0.65,
      dwelling: false, dwellUntilMs: 0,
    })),
    ...bwd.map(([f, t], i) => ({
      id: `S2 2000${i + 1}`, fromIdx: f, toIdx: t, dir: -1 as const,
      depMs: vNow - TRAVEL_V_MS * 0.6,  arrMs: vNow + TRAVEL_V_MS * 0.4,
      dwelling: false, dwellUntilMs: 0,
    })),
  ];
  return all;
}

// ─── Component ────────────────────────────────────────────────────────────────
const S2LiveMap: React.FC = () => {
  const navigate = useNavigate();

  const pathRef      = useRef<SVGPathElement>(null);
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Arc-length map: EVA → distance along path (in SVG path units)
  const distByEva = useRef<Record<string, number>>({});
  const trainsRef = useRef<TrainState[]>([]);
  const rafRef    = useRef<number>(0);

  // Virtual clock (accelerated time for test animation)
  const speedRef         = useRef(10);
  const anchorRealRef    = useRef(Date.now());
  const anchorVirtualRef = useRef(Date.now());

  const [speedDisplay, setSpeedDisplay] = useState(10);
  const [initialized,  setInitialized]  = useState(false);

  function vNowMs() {
    return anchorVirtualRef.current + (Date.now() - anchorRealRef.current) * speedRef.current;
  }

  // ── Step 1: build arc-length map from SVG path after mount ────────────────
  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;

    const L = path.getTotalLength();
    const SAMPLES = 2500;

    S2_STATIONS.forEach(st => {
      let bestS = 0, bestD2 = Infinity;
      for (let i = 0; i <= SAMPLES; i++) {
        const s = (i / SAMPLES) * L;
        const p = path.getPointAtLength(s);
        const dx = p.x - st.cx, dy = p.y - st.cy;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD2) { bestD2 = d2; bestS = s; }
      }
      distByEva.current[st.eva] = bestS;
    });

    trainsRef.current = makeFakeTrains(vNowMs());
    setInitialized(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 2: keep canvas pixel dimensions in sync with container size ───────
  useEffect(() => {
    const container = containerRef.current;
    const canvas    = canvasRef.current;
    if (!container || !canvas) return;

    const ctx = canvas.getContext('2d')!;

    function resize() {
      const r   = container!.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas!.width           = Math.round(r.width  * dpr);
      canvas!.height          = Math.round(r.height * dpr);
      canvas!.style.width     = r.width  + 'px';
      canvas!.style.height    = r.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();
    return () => ro.disconnect();
  }, []);

  // ── Step 3: animation loop (runs after arc-length map is ready) ────────────
  useEffect(() => {
    if (!initialized) return;

    const path      = pathRef.current!;
    const canvas    = canvasRef.current!;
    const container = containerRef.current!;
    const ctx       = canvas.getContext('2d')!;
    const totalL    = path.getTotalLength();

    // Convert SVG viewBox coordinates → canvas CSS pixels
    function svgToCanvas(pt: { x: number; y: number }) {
      const r = container.getBoundingClientRect();
      return { x: (pt.x / 2500) * r.width, y: (pt.y / 1750) * r.height };
    }

    // Draw a single rotated train rectangle at arc-length position s
    function drawTrain(s: number, label: string) {
      const pt     = path.getPointAtLength(s);
      const ptAhead = path.getPointAtLength(Math.min(totalL, s + 8));
      const p  = svgToCanvas(pt);
      const p2 = svgToCanvas(ptAhead);
      const ang = Math.atan2(p2.y - p.y, p2.x - p.x);

      const w = 20, h = 11, r = 3;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang);

      // Rounded rectangle body
      const x = -w / 2, y = -h / 2;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y,     x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h,     x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y,         x + r, y);
      ctx.closePath();
      ctx.fillStyle = '#e3051b';
      ctx.fill();

      // White front-window indicator
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fillRect(w / 2 - 5, -2, 3, 4);

      ctx.restore();

      // Short label (last 4 chars of journey ID)
      ctx.font = '10px "IBM Plex Mono", monospace';
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText(label, p.x + 13, p.y + 4);
    }

    // Advance train states: dwell → depart, moving → arrive
    function advanceTrains(vNow: number) {
      trainsRef.current = trainsRef.current.map(t => {
        if (t.dwelling) {
          if (vNow < t.dwellUntilMs) return t;
          // Depart: continue in same direction, flip at terminus
          let newDir = t.dir;
          let newTo  = t.fromIdx + t.dir;
          if (newTo < 0)  { newDir =  1; newTo = 1; }
          if (newTo >= N) { newDir = -1; newTo = N - 2; }
          return { ...t, toIdx: newTo, dir: newDir, dwelling: false, depMs: vNow, arrMs: vNow + TRAVEL_V_MS };
        }
        // Moving: check arrival
        if (vNow >= t.arrMs) {
          return { ...t, fromIdx: t.toIdx, dwelling: true, dwellUntilMs: vNow + DWELL_V_MS };
        }
        return t;
      });
    }

    function frame() {
      const vNow = vNowMs();
      advanceTrains(vNow);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const t of trainsRef.current) {
        const fromEva = S2_STATIONS[t.fromIdx].eva;
        const s0 = distByEva.current[fromEva];
        if (s0 == null) continue;

        let s: number;
        if (t.dwelling) {
          s = s0;
        } else {
          const toEva = S2_STATIONS[t.toIdx].eva;
          const s1 = distByEva.current[toEva];
          if (s1 == null) continue;
          const progress = clamp01((vNow - t.depMs) / (t.arrMs - t.depMs));
          s = s0 + (s1 - s0) * progress;
        }

        drawTrain(s, t.id.slice(-4));
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [initialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Speed slider: re-anchor virtual clock on change ────────────────────────
  function handleSpeedChange(e: React.ChangeEvent<HTMLInputElement>) {
    anchorVirtualRef.current = vNowMs();
    anchorRealRef.current    = Date.now();
    const v = parseFloat(e.target.value) || 1;
    speedRef.current = v;
    setSpeedDisplay(v);
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#0d1b2a',
      fontFamily: '"IBM Plex Sans", "Helvetica Neue", sans-serif',
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      <Topbar menuState={false} openAction={() => {}} />

      <Box sx={{ pt: '64px', px: { xs: 2, md: 6 }, pb: 6 }}>

        {/* Back nav */}
        <Box
          onClick={() => navigate('/analysis')}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            mt: 3, mb: 3, cursor: 'pointer',
            color: 'rgba(255,255,255,0.35)',
            fontFamily: '"IBM Plex Mono"', fontSize: '11px', letterSpacing: '0.1em',
            '&:hover': { color: '#f0a500' }, transition: 'color 0.2s',
          }}
        >
          ← ANALYSE
        </Box>

        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{
              fontFamily: '"IBM Plex Mono"', fontSize: '11px',
              letterSpacing: '0.2em', color: '#f0a500', textTransform: 'uppercase', mb: 0.5,
            }}>
              Stuttgart S-Bahn · S2
            </Typography>
            <Typography sx={{
              fontFamily: '"IBM Plex Sans"', fontSize: { xs: '22px', md: '28px' },
              fontWeight: 300, color: '#fff', lineHeight: 1.2,
            }}>
              Live Map{'  '}
              <Box component="span" sx={{
                fontSize: '11px', color: 'rgba(255,255,255,0.25)',
                fontFamily: '"IBM Plex Mono"', letterSpacing: '0.1em',
              }}>
                TESTDATEN · {S2_STATIONS.length} Haltestellen
              </Box>
            </Typography>
          </Box>

          {/* Speed control */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '6px', px: 2.5, py: 1.5,
          }}>
            <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>
              GESCHW.
            </Typography>
            <input
              type="range" min={1} max={60} step={1} value={speedDisplay}
              onChange={handleSpeedChange}
              style={{ width: 90, accentColor: '#f0a500', cursor: 'pointer' }}
            />
            <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '14px', fontWeight: 500, color: '#f0a500', minWidth: 32 }}>
              {speedDisplay}×
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{
          height: '1px',
          background: 'linear-gradient(90deg, #f0a500 0%, rgba(240,165,0,0.1) 40%, transparent 100%)',
          mb: 3,
        }} />

        {/* Map container: SVG + Canvas overlay */}
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
            width: '100%',
            borderRadius: '6px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: '#070f18',
          }}
        >
          {/* Inline SVG — path ref needed for getPointAtLength() */}
          <svg
            viewBox="0 0 2500 1750"
            width="100%"
            style={{ display: 'block' }}
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              ref={pathRef}
              fill="none"
              strokeWidth="11.5472"
              strokeLinecap="round"
              strokeLinejoin="round"
              stroke="#e3051b"
              strokeOpacity="0.6"
              d={S2_PATH_D}
            />
            {S2_STATIONS.map(st => (
              <circle
                key={st.eva}
                cx={st.cx} cy={st.cy} r={11}
                fill="#ffffff" stroke="#ffffff" strokeWidth={3}
              />
            ))}
          </svg>

          {/* Canvas overlay for animated train markers */}
          <canvas
            ref={canvasRef}
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          />

          {!initialized && (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(7,15,24,0.85)',
            }}>
              <Typography sx={{
                fontFamily: '"IBM Plex Mono"', fontSize: '12px',
                color: 'rgba(255,255,255,0.35)', letterSpacing: '0.15em',
              }}>
                INITIALISIERUNG…
              </Typography>
            </Box>
          )}
        </Box>

        {/* Legend */}
        <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {[
            { color: '#e3051b', shape: 'rect', label: 'S2 Zug (Testdaten, 6 Züge)' },
            { color: '#ffffff', shape: 'circle', label: 'Haltestelle' },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: item.shape === 'rect' ? 16 : 10,
                height: 10,
                borderRadius: item.shape === 'rect' ? '2px' : '50%',
                backgroundColor: item.color,
                flexShrink: 0,
              }} />
              <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

      </Box>
    </Box>
  );
};

export default S2LiveMap;
